import React, { useMemo, useState } from 'react';
import { colors } from '../../styles/tokens';
import { useEsMovil } from '../../hooks/useEsMovil';
import { fmtLugar, movimiento, ORDEN_BLOQUES, ROTULO_BLOQUE } from '../../utils/scorecard';
import type { RankingFila, RankingScorecard } from '../../types/scorecard.types';

export interface VistaRankingProps {
  ranking: RankingScorecard;
  /** La tienda elegida en el panel: va resaltada. */
  llaveActual: string;
  onAbrirTienda: (cadena: string, num: number) => void;
}

type Orden = 'lugar' | 'puntaje' | 'tienda' | 'resultados' | 'productividad' | 'cultura' | 'operacion';

const llave = (f: RankingFila) => `${f.cadena}|${f.num}`;
const puntajeBloque = (f: RankingFila, b: string) => f.porBloque.find((x) => x.bloque === b)?.puntaje ?? null;
const fmtPts = (n: number | null) => (n === null ? '—' : n.toLocaleString('es-MX', { maximumFractionDigits: 1 }));

/**
 * Ranking de tiendas de una semana dentro de la cadena o la regional. Las filas
 * se limitan al alcance de la cuenta; los lugares son sobre el grupo completo
 * («5 de 80»). Clic (o Enter) en una tienda abre su scorecard.
 */
const VistaRanking: React.FC<VistaRankingProps> = ({ ranking, llaveActual, onAbrirTienda }) => {
  const esMovil = useEsMovil();
  const [orden, setOrden] = useState<Orden>('lugar');
  const [asc, setAsc] = useState(true);

  const filas = useMemo(() => {
    const valor = (f: RankingFila): number | string => {
      if (orden === 'tienda') return f.tienda;
      if (orden === 'lugar') return f.lugar?.lugar ?? Number.MAX_SAFE_INTEGER;
      if (orden === 'puntaje') return f.puntaje ?? -1;
      return puntajeBloque(f, orden) ?? -1;
    };
    return [...ranking.filas].sort((a, b) => {
      const va = valor(a);
      const vb = valor(b);
      const c = typeof va === 'string' ? va.localeCompare(String(vb), 'es') : (va as number) - (vb as number);
      return asc ? c : -c;
    });
  }, [ranking.filas, orden, asc]);

  const ordenarPor = (o: Orden) => {
    if (o === orden) setAsc(!asc);
    else {
      setOrden(o);
      // Lugar y tienda suben; los puntajes bajan (el mejor primero).
      setAsc(o === 'lugar' || o === 'tienda');
    }
  };
  const aria = (o: Orden): 'ascending' | 'descending' | 'none' => (o === orden ? (asc ? 'ascending' : 'descending') : 'none');

  const cabecera = (o: Orden, texto: string, alinear: 'left' | 'right' = 'right') => (
    <th scope="col" aria-sort={aria(o)} style={{ ...thStyle, textAlign: alinear }}>
      <button type="button" onClick={() => ordenarPor(o)} style={btnOrden}>
        {texto}
        {o === orden ? (asc ? ' ↑' : ' ↓') : ''}
      </button>
    </th>
  );

  const grupoTexto = ranking.grupo === 'cadena' ? `cadena ${ranking.nombreGrupo}` : `regional ${ranking.nombreGrupo}`;

  if (esMovil) {
    return (
      <div data-testid="ranking-tarjetas" style={{ display: 'grid', gap: 10 }}>
        <p style={{ margin: 0, fontSize: 12.5, color: colors.texto.secondary }}>
          {ranking.filas.length} de {ranking.totalGrupo} tiendas de la {grupoTexto}.
        </p>
        {filas.map((f) => {
          const mov = movimiento(f.lugar?.lugar, f.lugarAnterior);
          const actual = llave(f) === llaveActual;
          return (
            <button
              type="button"
              key={llave(f)}
              onClick={() => onAbrirTienda(f.cadena, f.num)}
              style={{
                textAlign: 'left',
                border: `1px solid ${actual ? colors.brandSoftBorder : colors.border}`,
                background: actual ? colors.brandSoftBg : colors.card,
                borderRadius: 14,
                padding: '12px 14px',
                fontFamily: 'inherit',
                cursor: 'pointer',
                minWidth: 0,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontWeight: 800 }}>
                  {f.lugar ? `#${f.lugar.lugar}` : '—'} · {f.cadena} {f.num} {f.tienda}
                </span>
                <span style={{ fontWeight: 800, color: mov.color }}>{mov.texto}</span>
              </div>
              <div style={{ fontSize: 13, color: colors.texto.secondary, marginTop: 2 }}>
                {fmtPts(f.puntaje)} pts{f.motivo ? ` · ${f.motivo}` : ''}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginTop: 6, fontSize: 12 }}>
                {ORDEN_BLOQUES.map((b) => (
                  <span key={b} style={{ color: colors.texto.secondary }}>
                    {ROTULO_BLOQUE[b]} <strong style={{ color: colors.texto.strong }}>{fmtPts(puntajeBloque(f, b))}</strong>
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div data-testid="ranking-tabla">
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <caption style={{ textAlign: 'left', fontSize: 12.5, color: colors.texto.secondary, paddingBottom: 8 }}>
          {ranking.filas.length} de {ranking.totalGrupo} tiendas de la {grupoTexto}. Los indicadores en validación no cuentan en el puntaje.
        </caption>
        <thead>
          <tr>
            {cabecera('lugar', 'Lugar', 'left')}
            {cabecera('tienda', 'Tienda', 'left')}
            <th scope="col" style={{ ...thStyle, textAlign: 'left' }}>
              Supervisor
            </th>
            {cabecera('puntaje', 'Puntaje')}
            {ORDEN_BLOQUES.map((b) => (
              <React.Fragment key={b}>{cabecera(b, ROTULO_BLOQUE[b])}</React.Fragment>
            ))}
            <th scope="col" style={thStyle}>
              Vs semana anterior
            </th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => {
            const mov = movimiento(f.lugar?.lugar, f.lugarAnterior);
            const actual = llave(f) === llaveActual;
            return (
              <tr
                key={llave(f)}
                tabIndex={0}
                onClick={() => onAbrirTienda(f.cadena, f.num)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onAbrirTienda(f.cadena, f.num);
                }}
                style={{ cursor: 'pointer', background: actual ? colors.brandSoftBg : undefined }}
                data-testid={`ranking-fila-${f.cadena}-${f.num}`}
              >
                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 800 }}>{f.lugar ? fmtLugar(f.lugar) : '—'}</td>
                <td style={{ ...tdStyle, textAlign: 'left' }}>
                  <span style={{ fontWeight: 800 }}>
                    {f.cadena} {f.num} {f.tienda}
                  </span>
                  {actual && <span style={{ marginLeft: 8, fontSize: 11, color: colors.brand, fontWeight: 800 }}>tu tienda</span>}
                  {f.motivo && <span style={{ display: 'block', fontSize: 11.5, color: colors.texto.tertiary }}>{f.motivo}</span>}
                </td>
                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 600, color: colors.texto.secondary }}>{f.supervisor ?? '—'}</td>
                <td style={{ ...tdStyle, fontWeight: 800 }}>{fmtPts(f.puntaje)}</td>
                {ORDEN_BLOQUES.map((b) => (
                  <td key={b} style={tdStyle}>
                    {fmtPts(puntajeBloque(f, b))}
                  </td>
                ))}
                <td style={{ ...tdStyle, color: mov.color, fontWeight: 800 }}>{mov.texto || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  textAlign: 'right',
  fontSize: 10.5,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: colors.texto.quaternary,
  fontWeight: 800,
  padding: '8px 10px',
  borderBottom: `1px solid ${colors.rowBorder}`,
  whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = {
  textAlign: 'right',
  padding: '9px 10px',
  borderBottom: `1px solid ${colors.rowBorder}`,
  fontVariantNumeric: 'tabular-nums',
  fontSize: 13,
  fontWeight: 700,
  color: colors.texto.strong,
};
const btnOrden: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  color: 'inherit',
  letterSpacing: 'inherit',
  textTransform: 'inherit',
  cursor: 'pointer',
};

export default VistaRanking;
